import { useCallback, useEffect, useState } from "react";

import { API_PROBATION_CYCLES } from "../../../../../config/Endpoints.js";
import { getAuthToken } from "../../../../../utils/Storage.js";
import { ApiError, getApi } from "../../../../utils/api.ts";
import {
  parseProbationCyclesPagedBody,
  type ProbationCycleRow,
} from "../schemas/probation-cycle-list.schema.ts";

function accessToken(): string | undefined {
  const t = getAuthToken("accessToken");
  return t != null && t !== "" ? t : undefined;
}

export type UseProbationCyclesParams = {
  page: number;
  pageSize: number;
  status?: string;
  employeeId?: number | string;
  managerId?: number | string;
};

export function useProbationCycles({
  page,
  pageSize,
  status,
  employeeId,
  managerId,
}: UseProbationCyclesParams) {
  const [rows, setRows] = useState<ProbationCycleRow[]>([]);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(
    async (signal?: AbortSignal) => {
      setLoading(true);
      setError(null);
      const apiPage = Math.max(0, page - 1);
      const params = new URLSearchParams({
        page: String(apiPage),
        size: String(pageSize),
      });
      if (status != null && status !== "") params.set("status", status);
      if (employeeId != null && employeeId !== "") {
        params.set("employeeId", String(employeeId));
      }
      if (managerId != null && managerId !== "") {
        params.set("managerId", String(managerId));
      }
      const url = `${API_PROBATION_CYCLES}?${params.toString()}`;
      try {
        const { data } = await getApi<unknown>(url, accessToken(), "json", signal);
        const parsed = parseProbationCyclesPagedBody(data);
        setRows(parsed.rows);
        setTotalElements(parsed.totalElements);
      } catch (e) {
        const msg = e instanceof ApiError ? e.message : "Failed to load probation cycles.";
        setError(msg);
        setRows([]);
        setTotalElements(0);
      } finally {
        setLoading(false);
      }
    },
    [page, pageSize, status, employeeId, managerId],
  );

  useEffect(() => {
    const ac = new AbortController();
    void refetch(ac.signal);
    return () => ac.abort();
  }, [refetch]);

  return { rows, totalElements, loading, error, refetch };
}
